import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    Alert,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Divider,
    Chip,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../components/AuthContext';
import { API_BASE_URL } from '../components/api.js';

// ─── helpers ────────────────────────────────────────────────────────────────

const toLocalDate = (value) =>
    value ? new Date(value.getFullYear(), value.getMonth(), value.getDate()) : null;

const formatDate = (date) =>
    date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : null;

const formatDisplay = (dateStr) =>
    dateStr ? dateStr.split('-').reverse().join('/') : '';

const formatTime = (timeDate) => {
    if (!timeDate) return null;
    return `${String(timeDate.getHours()).padStart(2, '0')}:${String(timeDate.getMinutes()).padStart(2, '0')}`;
};

const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const f = parseFloat(value);
    if (isNaN(f)) return 'N/A';
    return `R$ ${f.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
};

const GRANULARITY_OPTIONS = [
    { value: 5, label: '5 em 5 minutos' },
    { value: 10, label: '10 em 10 minutos' },
    { value: 15, label: '15 em 15 minutos' },
    { value: 30, label: '30 em 30 minutos' },
    { value: 60, label: '60 em 60 minutos' },
];

// ─── TimeRangeInput ──────────────────────────────────────────────────────────

function TimeRangeInput({ label, startValue, endValue, onStartChange, onEndChange, color = 'primary' }) {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: `${color}.light`,
                backgroundColor: `${color}.50`,
                minWidth: 260,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon fontSize="small" color={color} />
                <Typography variant="subtitle2" fontWeight="bold" color={`${color}.main`}>
                    {label}
                    <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                    >
                        (opcional)
                    </Typography>
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TimePicker
                    label="Início"
                    value={startValue}
                    onChange={onStartChange}
                    ampm={false}
                    minutesStep={1}
                    slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    às
                </Typography>
                <TimePicker
                    label="Fim"
                    value={endValue}
                    onChange={onEndChange}
                    ampm={false}
                    minutesStep={1}
                    slotProps={{ textField: { size: 'small', sx: { width: 130 } } }}
                />
            </Box>
        </Box>
    );
}

// ─── component ──────────────────────────────────────────────────────────────

function VendasHoras() {
    const { authenticatedFetch, logout } = useAuth();

    const [mode, setMode] = useState('year');
    const [dateRef, setDateRef] = useState(new Date());
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [lastClosedDate, setLastClosedDate] = useState(null);

    // Time period 1
    const [time1Start, setTime1Start] = useState(null);
    const [time1End, setTime1End] = useState(null);

    // Time period 2
    const [time2Start, setTime2Start] = useState(null);
    const [time2End, setTime2End] = useState(null);

    // Granularidade (só usada quando há filtro de horário)
    const [granularity, setGranularity] = useState(60);

    const [resultado, setResultado] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Busca a última data fechada ao montar
    useEffect(() => {
        const fetchLastDate = async () => {
            try {
                const response = await authenticatedFetch(
                    `${API_BASE_URL}/api/dashboard-periodo-ultima-data-fechada/`
                );
                if (!response.ok) {
                    if (response.status === 401) logout();
                    throw new Error('Erro ao buscar última data fechada');
                }
                const data = await response.json();
                const [y, m, d] = data.last_closed_date.split('-');
                setLastClosedDate(new Date(Number(y), Number(m) - 1, Number(d)));
            } catch (err) {
                setError('Não foi possível identificar a última data fechada.');
            }
        };
        fetchLastDate();
    }, [authenticatedFetch, logout]);

    const handleModeChange = (_, newMode) => {
        if (newMode) setMode(newMode);
        setResultado(null);
        setError(null);
    };

    const validateTimePair = (start, end, label) => {
        const hasStart = start !== null;
        const hasEnd = end !== null;
        if (hasStart && !hasEnd) return `Informe o horário de fim do ${label}.`;
        if (!hasStart && hasEnd) return `Informe o horário de início do ${label}.`;
        if (hasStart && hasEnd && start >= end) return `O início do ${label} deve ser anterior ao fim.`;
        return null;
    };

    const handleBuscar = async () => {
        if (!lastClosedDate) {
            setError('Sistema ainda não identificou a última data válida.');
            return;
        }

        const err1 = validateTimePair(time1Start, time1End, 'Período 1');
        if (err1) { setError(err1); return; }

        const err2 = validateTimePair(time2Start, time2End, 'Período 2');
        if (err2) { setError(err2); return; }

        setError(null);
        setResultado(null);

        const payload = { mode, last_closed_date: formatDate(lastClosedDate) };

        if (mode === 'period') {
            if (!startDate || !endDate) {
                setError('Selecione data inicial e final.');
                return;
            }
            payload.start_date = formatDate(startDate);
            payload.end_date = formatDate(endDate);
        } else {
            payload.reference_date = formatDate(dateRef);
        }

        const hasTimeFilter = (time1Start && time1End) || (time2Start && time2End);

        if (time1Start && time1End) {
            payload.time_period_1 = {
                start: formatTime(time1Start),
                end: formatTime(time1End),
            };
        }
        if (time2Start && time2End) {
            payload.time_period_2 = {
                start: formatTime(time2Start),
                end: formatTime(time2End),
            };
        }

        // Envia granularidade apenas quando há filtro de horário
        if (hasTimeFilter) {
            payload.granularity_minutes = granularity;
        }

        setLoading(true);
        try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/vendas-horas/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                if (response.status === 401) logout();
                const err = await response.json().catch(() => ({}));
                throw new Error(err.error || `Erro ${response.status}`);
            }
            const data = await response.json();
            setResultado(data);
        } catch (err) {
            setError(`Falha ao carregar dados: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const hasAnyTimeFilter = (time1Start && time1End) || (time2Start && time2End);

    // ─── render tabela ──────────────────────────────────────────────────────

    const renderTable = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography>Carregando dados...</Typography>
                </Box>
            );
        }

        if (!resultado) return null;

        const { rows, period_start, period_end, time_filters_applied } = resultado;

        if (!rows || rows.length === 0) {
            return (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Nenhuma venda encontrada para o período e horários selecionados.
                </Alert>
            );
        }

        return (
            <Box>
                {/* Chips de horários filtrados */}
                {time_filters_applied && time_filters_applied.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            Horários filtrados:
                        </Typography>
                        {time_filters_applied.map((tf, i) => (
                            <Chip
                                key={i}
                                icon={<AccessTimeIcon />}
                                label={`${tf.start} – ${tf.end}`}
                                size="small"
                                color={i === 0 ? 'primary' : 'secondary'}
                                variant="outlined"
                            />
                        ))}
                    </Box>
                )}

                <TableContainer component={Paper} elevation={0} sx={{ width: 'fit-content' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', width: 'auto' }}>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            {/* Linha 1: cabeçalhos */}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', width: 160 }}>
                                    Horário
                                </TableCell>
                                <TableCell
                                    align="center"
                                    sx={{
                                        fontWeight: 'bold',
                                        borderLeft: '1px solid #ddd',
                                        width: 160,
                                    }}
                                >
                                    Período Base
                                </TableCell>
                                <TableCell align="center" rowSpan={2} sx={{ fontWeight: 'bold', bgcolor: '#f0f7ff' }}>PRIMEIRO</TableCell>
                                <TableCell align="center" rowSpan={2} sx={{ fontWeight: 'bold', bgcolor: '#f0f7ff' }}>ÚLTIMO</TableCell>
                                <TableCell align="center" rowSpan={2} sx={{ fontWeight: 'bold', bgcolor: '#f0f7ff' }}>CUPONS</TableCell>
                            </TableRow>

                            {/* Linha 2: datas do período base */}
                            <TableRow>
                                <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', py: 0.5 }} />
                                <TableCell
                                    align="center"
                                    sx={{
                                        borderLeft: '1px solid #ddd',
                                        fontSize: '0.75rem',
                                        color: 'primary.main',
                                        fontWeight: 'bold',
                                        py: 0.5,
                                    }}
                                >
                                    {formatDisplay(period_start)}
                                    {period_start !== period_end && (
                                        <> → {formatDisplay(period_end)}</>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows.map((row, idx) => (
                                <TableRow key={idx} hover>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {row.time_label}
                                    </TableCell>
                                    <TableCell align="right" sx={{ borderLeft: '1px solid #ddd' }}>
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatValue(row.base_value)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ color: 'text.secondary' }}>{row.primeiro_horario}</TableCell>
                                    <TableCell align="center" sx={{ color: 'text.secondary' }}>{row.ultimo_horario}</TableCell>
                                    <TableCell align="center">
                                        <Chip label={row.qtd_cupons} size="small" variant="outlined" color="primary" />
                                    </TableCell>
                                </TableRow>
                            ))}

                            {/* Linha de TOTAL se houver mais de uma linha */}
                            {rows.length > 1 && resultado.total && (
                                <TableRow
                                    sx={{ backgroundColor: '#f0f4ff', borderTop: '2px solid #ddd' }}
                                >
                                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                        Total
                                    </TableCell>
                                    <TableCell align="right" sx={{ borderLeft: '1px solid #ddd' }}>
                                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                                            {formatValue(resultado.total.base_value)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    // ─── render principal ───────────────────────────────────────────────────

    return (
        <Box sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h5" fontWeight="bold">
                Vendas por Horário
            </Typography>

            {lastClosedDate && (
                <Alert severity="info">
                    Última data fechada: {lastClosedDate.toLocaleDateString('pt-BR')}
                </Alert>
            )}
            {error && <Alert severity="error">{error}</Alert>}

            <Paper elevation={3} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, width: 'fit-content', minWidth: 200 }}>

                {/* Modo de período */}
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Modo de período
                    </Typography>
                    <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange}>
                        <ToggleButton value="year">Ano</ToggleButton>
                        <ToggleButton value="month">Mês</ToggleButton>
                        <ToggleButton value="period">Período</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                {/* Seletores de data */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            {mode === 'period' ? 'Intervalo de datas' : 'Data de referência'}
                        </Typography>
                        {mode === 'period' ? (
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <DatePicker
                                    label="Data Inicial"
                                    value={startDate}
                                    onChange={(v) => setStartDate(toLocalDate(v))}
                                    format="dd/MM/yyyy"
                                />
                                <DatePicker
                                    label="Data Final"
                                    value={endDate}
                                    onChange={(v) => setEndDate(toLocalDate(v))}
                                    format="dd/MM/yyyy"
                                />
                            </Box>
                        ) : (
                            <DatePicker
                                label="Data de Referência"
                                value={dateRef}
                                onChange={(v) => setDateRef(toLocalDate(v))}
                                format="dd/MM/yyyy"
                            />
                        )}
                    </Box>

                    <Divider />

                    {/* Filtros de horário */}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Filtros de horário
                            {!hasAnyTimeFilter && (
                                <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                                    — sem filtro: exibe por hora (00:00–23:00) somente com vendas
                                </Typography>
                            )}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <TimeRangeInput
                                label="Período 1"
                                startValue={time1Start}
                                endValue={time1End}
                                onStartChange={setTime1Start}
                                onEndChange={setTime1End}
                                color="primary"
                            />
                            <TimeRangeInput
                                label="Período 2"
                                startValue={time2Start}
                                endValue={time2End}
                                onStartChange={setTime2Start}
                                onEndChange={setTime2End}
                                color="secondary"
                            />
                        </Box>
                    </Box>

                    {/* Select de granularidade — só aparece quando há filtro de horário */}
                    {hasAnyTimeFilter && (
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Divisão do intervalo
                            </Typography>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>Granularidade</InputLabel>
                                <Select
                                    value={granularity}
                                    label="Granularidade"
                                    onChange={(e) => setGranularity(e.target.value)}
                                >
                                    {GRANULARITY_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </LocalizationProvider>

                <Button
                    variant="contained"
                    onClick={handleBuscar}
                    disabled={loading}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    {loading ? 'Buscando...' : 'Buscar'}
                </Button>
            </Paper>

            {(resultado || loading) && (
                <Paper elevation={3} sx={{ p: 2, overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
                    {renderTable()}
                </Paper>
            )}
        </Box>
    );
}

export default VendasHoras;